"use client";

import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { ArrowUpRight, BarChart3, ChevronLeft, ChevronRight, Route, Tag } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Check } from "lucide-react";
import {
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { gsap } from "@/components/sections/kit/motion";
import { roadmapItem } from "@/lib/roadmap";
import {
  CHANNEL_BARS,
  LINK_DOMAIN,
  LINK_SLUG,
  QR_PATH,
  RAIL_STAGES,
  RAIL_STATUS,
  TOTAL_EVENTS,
  TOTAL_FMT,
} from "./data";

const BRAND = "var(--color-brand-600)";
const WEBHOOKS_ETA = roadmapItem("webhooks").promised;

/** The artifact pane's cumulative job rows — one per stage. */
const ART_ROWS: { label: string; Icon: LucideIcon; stage: string; value: ReactNode }[] = [
  { label: "UTMs", Icon: Tag, stage: "01", value: "campaign on the destination (UTM builder) · ?utm_source per channel" },
  { label: "Routing", Icon: Route, stage: "02", value: "4 rules — OS, geo, time of week" },
  {
    label: "Traffic",
    Icon: BarChart3,
    stage: "03",
    value: (
      <>
        <span data-traffic-count>0</span> taps + scans · 4 channels
      </>
    ),
  },
  { label: "Reads out", Icon: ArrowUpRight, stage: "04", value: "Dashboard · request logs · MCP" },
];

/** The branded link chip used inside stage visuals. */
function LinkChip({ suffix }: { suffix?: string }) {
  return (
    <div className="flex items-center gap-2.5 whitespace-nowrap rounded-ref-sm border px-[18px] py-3 [background:color-mix(in_srgb,var(--color-brand-600)_6%,white)] [border-color:color-mix(in_srgb,var(--color-brand-600)_35%,transparent)]">
      <span className="font-jetbrains text-[13px]">
        <span className="font-bold text-brand-600">{LINK_DOMAIN}</span>
        {LINK_SLUG}
      </span>
      {suffix && <span className="text-[11px] text-dim max-sm:hidden">{suffix}</span>}
    </div>
  );
}

const FLOW_LINE = "fill-none stroke-line-3 [stroke-dasharray:3_5] animate-flow-dash";

interface StagePanel {
  title: string;
  body: string;
  footer: string;
  visual: ReactNode;
}

const PANELS: StagePanel[] = [
  {
    title: "Plan — the brief becomes a link",
    body: "Set the destination and tag the campaign once with the UTM builder, then share the link with its own ?utm_source per channel. At click time rii.link records each channel's utm_source from the short link and the campaign tag from the destination, so every channel reports on its own.",
    footer: "utm_source read at click time — the spreadsheet column retires",
    visual: (
      <div className="w-full max-w-[560px] rounded-ref border border-line bg-surface px-6 py-5">
        <div className="border-b border-line pb-2.5 text-[12.5px] font-bold">
          Your brief — Spring Sale
        </div>
        <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3.5 gap-y-2 pt-3 text-[12.5px]">
          <span className="text-dim">Destination</span>
          <span className="font-jetbrains text-[11.5px]">spring.acme.com/sale</span>
          <span className="text-dim">Window</span>
          <span>May 2 → Jun 30</span>
          <span className="text-dim">Channels</span>
          <span>newsletter · bio · flyer · partner</span>
          <span className="text-dim">UTM set</span>
          <span className="font-jetbrains text-[11.5px]">campaign=spring-sale · source per channel</span>
        </div>
        <div className="mt-3">
          <LinkChip suffix="— created once, shared everywhere" />
        </div>
      </div>
    ),
  },
  {
    title: "Launch — every channel, one link",
    body: "Print it, mail it, paste it in bio. Routing rules open the right door for each visitor — the App Store for an iOS scan, the German page for Berlin — while the link never changes.",
    footer: "4 routing rules active — OS, geo, time of week",
    visual: (
      <div className="flex items-center max-sm:flex-col max-sm:items-start max-sm:gap-3">
        <div className="flex flex-none flex-col gap-2.5 max-sm:flex-row max-sm:flex-wrap">
          {CHANNEL_BARS.map((c) => (
            <span
              key={c.name}
              className="inline-flex items-center gap-2.5 whitespace-nowrap rounded-full border border-line-2 bg-surface px-4 py-2 text-[13px] font-semibold"
            >
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
              {{ newsletter: "Newsletter", "instagram-bio": "Instagram bio", "flyer-qr": "Flyer QR", partner: "Partner post" }[c.name]}
            </span>
          ))}
        </div>
        <svg width="120" height="176" className="flex-none max-sm:hidden" aria-hidden="true">
          <path d="M6 23 C 62 23, 62 88, 114 88" strokeWidth="1.5" className={FLOW_LINE} />
          <path d="M6 66 C 62 66, 62 88, 114 88" strokeWidth="1.5" className={FLOW_LINE} />
          <path d="M6 110 C 62 110, 62 88, 114 88" strokeWidth="1.5" className={FLOW_LINE} />
          <path d="M6 153 C 62 153, 62 88, 114 88" strokeWidth="1.5" className={FLOW_LINE} />
        </svg>
        <LinkChip />
      </div>
    ),
  },
  {
    title: "Measure — the scoreboard fills itself",
    body: "Clicks and scans land in one view, split by the utm_source each channel's URL carried. No spreadsheet reconciliation, no “which bitly was the flyer again?”",
    footer: "utm_source read from every click — full dashboard below ↓",
    visual: (
      <div className="flex w-full max-w-[620px] flex-col gap-3.5">
        {CHANNEL_BARS.map((b) => (
          <div
            key={b.name}
            className="grid grid-cols-[118px_minmax(0,1fr)_56px] items-center gap-3.5"
          >
            <span className="font-jetbrains text-[11.5px] text-muted">{b.name}</span>
            <span className="block h-[22px] overflow-hidden rounded-md border border-line bg-surface">
              <span
                className="block h-full rounded-[5px] opacity-85"
                style={{ width: `${b.w}%`, background: b.color }}
              />
            </span>
            <span className="text-right font-jetbrains text-[11.5px]">{b.n}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "Optimize — act while it runs",
    body: "Spot the winner while the campaign is still running — in the dashboard, or by asking your AI agent over MCP — then change the destination or routing on the same link. No reprint.",
    footer: `honest status: no native GA4 / Meta connectors — outbound webhooks are on the roadmap for ${WEBHOOKS_ETA}`,
    visual: (
      <div className="flex items-center max-sm:flex-col max-sm:items-start max-sm:gap-3">
        <div className="flex flex-none items-center gap-2.5 rounded-ref-sm border border-line-2 bg-surface px-[18px] py-3">
          <span className="font-jetbrains text-[13px]">{TOTAL_FMT} events</span>
        </div>
        <svg width="120" height="176" className="flex-none max-sm:hidden" aria-hidden="true">
          <path d="M6 88 C 56 88, 56 25, 114 25" strokeWidth="1.5" className={FLOW_LINE} />
          <path d="M6 88 L 114 88" strokeWidth="1.5" className={FLOW_LINE} />
          <path d="M6 88 C 56 88, 56 151, 114 151" strokeWidth="1.5" className={FLOW_LINE} />
        </svg>
        <div className="flex flex-col gap-2.5">
          {[
            { name: "Dashboard", pill: "LIVE", live: true },
            { name: "MCP", pill: "LIVE", live: true },
            { name: "Webhooks", pill: WEBHOOKS_ETA.toUpperCase(), live: false },
          ].map((o) => (
            <span
              key={o.name}
              className="inline-flex items-center gap-2.5 rounded-full border border-line-2 bg-surface px-4 py-2 text-[13px] font-semibold"
            >
              {o.name}
              <span
                className={`whitespace-nowrap rounded-full px-2 py-0.5 font-jetbrains text-[10px] tracking-[0.06em] ${
                  o.live
                    ? "text-data-green [background:color-mix(in_srgb,var(--color-data-green)_12%,white)]"
                    : "bg-surface-2 text-dim"
                }`}
              >
                {o.pill}
              </span>
            </span>
          ))}
        </div>
      </div>
    ),
  },
];

const SR_SUMMARY =
  "Interactive: one campaign link, go.acme.com/spring-sale, followed through four stages. The " +
  "card on the left is the same link the whole way — Plan sets the campaign tag on the " +
  "destination with the UTM builder and gives each channel its own utm_source on the short link; " +
  "rii.link records both at click time; Launch activates four routing rules (OS, country, time of week); " +
  "Measure counts 3,606 taps and scans across four channels, Optimize reads the numbers in the " +
  "dashboard or over MCP and edits the same link mid-campaign. Outbound webhooks are on the " +
  `roadmap for ${WEBHOOKS_ETA}; there are no native GA4 or Meta connectors.`;

/**
 * The page signature: a column-snapped horizontal rail (PLAN → LAUNCH →
 * MEASURE → OPTIMIZE) beside a fixed pane holding ONE shared artifact — the
 * campaign link — that relabels itself at each stage. Causality, not
 * transformation: nothing is recreated, the same link just picks up new jobs.
 */
export default function LifecycleRailSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const countedRef = useRef(false);
  const snapTimer = useRef(0);
  const scrollRaf = useRef(0);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);

  /** Count 0 → 3,606 the first time the story reaches MEASURE. */
  const maybeCount = (stage: number) => {
    if (stage < 2 || countedRef.current) return;
    countedRef.current = true;
    const el = paneRef.current?.querySelector<HTMLElement>("[data-traffic-count]");
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.textContent = TOTAL_FMT;
      return;
    }
    const proxy = { v: 0 };
    gsap.to(proxy, {
      v: TOTAL_EVENTS,
      duration: 1.1,
      ease: "power2.out",
      onUpdate: () => {
        el.textContent = Math.round(proxy.v).toLocaleString("en-US");
      },
    });
  };

  const setStage = (i: number) => {
    if (i !== activeRef.current) {
      activeRef.current = i;
      setActive(i);
    }
    maybeCount(i);
  };

  const goTo = (i: number) => {
    const el = railRef.current;
    if (!el) return;
    const stage = Math.max(0, Math.min(3, i));
    const target = stage * el.clientWidth;
    window.clearTimeout(snapTimer.current);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.scrollLeft = target;
    } else {
      // Chrome cancels smooth scrollTo under mandatory snap: lift snapping,
      // glide, then force the exact offset and restore the EXPLICIT literal
      // ('' would kill snapping for the session).
      el.style.scrollSnapType = "none";
      el.scrollTo({ left: target, behavior: "smooth" });
      snapTimer.current = window.setTimeout(() => {
        el.scrollLeft = target;
        el.style.scrollSnapType = "x mandatory";
      }, 650);
    }
    setStage(stage);
  };

  const onRailScroll = () => {
    if (scrollRaf.current) return;
    scrollRaf.current = requestAnimationFrame(() => {
      scrollRaf.current = 0;
      const el = railRef.current;
      if (!el || !el.clientWidth) return;
      setStage(Math.max(0, Math.min(3, Math.round(el.scrollLeft / el.clientWidth))));
    });
  };

  const status = RAIL_STATUS[active];
  const live = active > 0;

  return (
    <SectionShell id="lifecycle" className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px">
      <div ref={rootRef} className="flex flex-col">
        <SectionHead
          eyebrow="A sample campaign"
          title="One link becomes the whole campaign."
          lead={
            <>
              Follow <span className="font-jetbrains text-[0.92em]">go.acme.com/spring-sale</span>{" "}
              from brief to results. The card on the left is the same link the entire way — it
              never gets recreated, it just picks up new jobs.
            </>
          }
        />

        {/* Controls + pane + rail share ONE grid block, so the switcher sits
            directly above the content it drives instead of floating a free
            row (+ row-centering padding) away from it. */}
        <div data-grid-block className="max-md:mt-10">
          <div className="flex w-full items-center gap-[18px]">
            <div className="flex max-w-full gap-1 overflow-x-auto rounded-full border border-line bg-surface-2 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {RAIL_STAGES.map((name, i) => (
                <button
                  key={name}
                  type="button"
                  aria-label={`Go to stage ${i + 1} — ${name}`}
                  aria-pressed={i === active}
                  onClick={() => goTo(i)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-[7px] font-jetbrains text-[11px] font-semibold tracking-[0.08em] transition-colors ${
                    i === active ? "bg-ink text-white" : "text-muted hover:text-ink"
                  }`}
                >
                  <span className={`text-[10px] ${i === active ? "text-brand-400" : "text-dim"}`}>
                    0{i + 1}
                  </span>
                  {name}
                </button>
              ))}
            </div>
            <span className="font-jetbrains text-[11px] text-dim max-sm:hidden">
              0{active + 1} / 04
            </span>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                aria-label="Previous stage"
                disabled={active === 0}
                onClick={() => goTo(active - 1)}
                className="grid h-9 w-9 place-items-center rounded-full border border-line-2 bg-surface transition-colors hover:border-brand-600 disabled:opacity-35 disabled:hover:border-line-2"
              >
                <ChevronLeft size={15} strokeWidth={2.2} />
              </button>
              <button
                type="button"
                aria-label="Next stage"
                disabled={active === 3}
                onClick={() => goTo(active + 1)}
                className="grid h-9 w-9 place-items-center rounded-full border border-line-2 bg-surface transition-colors hover:border-brand-600 disabled:opacity-35 disabled:hover:border-line-2"
              >
                <ChevronRight size={15} strokeWidth={2.2} />
              </button>
            </div>
          </div>

          {/* Artifact pane + rail. */}
          <div className="relative mt-6 grid items-stretch gap-6 lg:grid-cols-[400px_minmax(0,1fr)] lg:gap-12">
          <p className="sr-only">{SR_SUMMARY}</p>
          {/* the causality wire between pane and rail */}
          <div
            aria-hidden="true"
            className="absolute left-[400px] top-[120px] hidden h-2 w-12 items-center lg:flex"
          >
            <svg width="48" height="2" className="overflow-visible">
              <line
                x1="0"
                y1="1"
                x2="48"
                y2="1"
                strokeWidth="1.5"
                className="stroke-brand-600 [stroke-dasharray:3_5] animate-flow-dash"
              />
            </svg>
          </div>

          {/* ——— THE ARTIFACT — the same link, all four stages ——— */}
          <div
            ref={paneRef}
            aria-label={`The campaign link — ${LINK_DOMAIN}${LINK_SLUG}`}
            className="flex flex-col rounded-ref-lg border bg-surface p-6 shadow-[0_20px_50px_-20px_rgba(16,16,40,0.19)] transition-colors duration-300"
            style={{
              borderColor: live
                ? "color-mix(in srgb, var(--color-brand-600) 35%, transparent)"
                : "var(--color-line)",
            }}
          >
            <div className="flex items-center gap-2.5">
              <span
                className="inline-flex items-center gap-[7px] whitespace-nowrap rounded-full border px-3 py-1 font-jetbrains text-[10.5px] font-bold tracking-[0.1em] transition-colors duration-300"
                style={{
                  color: status.color,
                  borderColor: `color-mix(in srgb, ${status.color} 35%, transparent)`,
                  background: `color-mix(in srgb, ${status.color} 9%, white)`,
                }}
              >
                <span className="relative h-1.5 w-1.5 rounded-full" style={{ background: status.color }}>
                  {live && (
                    <span
                      className="absolute -inset-[2px] animate-status-pulse rounded-full"
                      style={{ background: `color-mix(in srgb, ${status.color} 40%, transparent)` }}
                    />
                  )}
                </span>
                {status.label}
              </span>
              <span className="font-jetbrains text-[11px] text-dim">{status.sub}</span>
            </div>

            <div className="mt-[18px] flex items-center gap-4">
              <svg
                viewBox="0 0 29 29"
                width="76"
                height="76"
                aria-hidden="true"
                className="flex-none rounded-[10px] border border-line bg-white p-1.5"
              >
                <path d={QR_PATH} fill="var(--color-ink)" />
              </svg>
              <div className="min-w-0">
                <div className="whitespace-nowrap font-jetbrains text-[17.5px] font-semibold max-sm:text-[15px]">
                  <span className="text-brand-600">{LINK_DOMAIN}</span>
                  {LINK_SLUG}
                </div>
                <div className="mt-1.5 text-[12.5px] text-muted">→ spring.acme.com/sale</div>
                <div className="mt-[3px] font-jetbrains text-[10.5px] text-dim">
                  created May 2 · 302 redirect
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              {ART_ROWS.map((r, i) => {
                const on = i === active;
                const done = i < active;
                return (
                  <div
                    key={r.label}
                    className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-2.5 rounded-ref-sm border px-3 py-2.5 transition-[opacity,background-color,border-color] duration-300"
                    style={{
                      opacity: on || done ? 1 : 0.42,
                      borderColor: on
                        ? "color-mix(in srgb, var(--color-brand-600) 40%, transparent)"
                        : "var(--color-line)",
                      background: on
                        ? "color-mix(in srgb, var(--color-brand-600) 5%, white)"
                        : "var(--color-surface)",
                    }}
                  >
                    <span
                      className="mt-px flex h-[18px] w-[18px] items-center justify-center rounded-full transition-colors duration-300"
                      style={{
                        background: on
                          ? BRAND
                          : done
                            ? "color-mix(in srgb, var(--color-brand-600) 12%, white)"
                            : "var(--color-surface-2)",
                        color: on ? "#fff" : done ? BRAND : "var(--color-dim)",
                      }}
                    >
                      {done ? <Check size={10} strokeWidth={3} /> : <r.Icon size={10} strokeWidth={2.6} />}
                    </span>
                    <span>
                      <span className="flex items-baseline justify-between gap-2.5">
                        <span className="text-[13px] font-bold">{r.label}</span>
                        <span
                          className="font-jetbrains text-[10px] tracking-[0.08em]"
                          style={{ color: on ? BRAND : "var(--color-dim)" }}
                        >
                          {r.stage}
                        </span>
                      </span>
                      <span className="mt-0.5 block font-jetbrains text-[11.5px] leading-[1.5] text-muted">
                        {r.value}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>

            <p className="mb-0 mt-auto pt-4 text-[12px] leading-[1.5] text-dim">
              Same link, four jobs. Nothing was duplicated, re-tagged or re-printed between stages.
            </p>
          </div>

          {/* ——— The rail — four column-snapped stage panels ——— */}
          <div
            ref={railRef}
            onScroll={onRailScroll}
            tabIndex={0}
            aria-label="Campaign stages — scroll horizontally"
            className="flex overflow-x-auto rounded-ref-lg [overscroll-behavior-x:contain] [scroll-snap-type:x_mandatory] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {PANELS.map((p, i) => (
              <div key={p.title} className="min-w-0 flex-none snap-start basis-full px-0.5 py-1">
                <div className="flex h-full flex-col rounded-ref-lg border border-line bg-surface-2 px-8 py-7 max-sm:px-5">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-jetbrains text-[11px] font-bold tracking-[0.14em] text-brand-600">
                      STAGE 0{i + 1}
                    </span>
                    <span className="font-onest text-[22px] font-extrabold tracking-[-0.02em] max-sm:text-[19px]">
                      {p.title}
                    </span>
                  </div>
                  <p className="mb-4 mt-2.5 max-w-[620px] text-[14px] leading-[1.6] text-muted [text-wrap:pretty]">
                    {p.body}
                  </p>
                  {/* The stage visual owns the panel's free space, centered
                      both ways — it should fill the stage, not hug a corner. */}
                  <div className="flex min-h-0 w-full flex-1 items-center justify-center py-3">
                    {p.visual}
                  </div>
                  <div className="pt-4 font-jetbrains text-[11px] text-dim">{p.footer}</div>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
